import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, Button, Card, Empty, ErrorNote, inputClass, Spinner } from "@/components/ui";
import { api, type DealRow } from "@/lib/api";
import { formatAcres, formatDate, formatMoneyCents, formatPct } from "@/lib/format";
import {
  DEAL_STAGE_LABEL,
  DEAL_STAGE_ORDER,
  dealStageTone,
  STRATEGY_LABEL,
} from "@/lib/dealStages";
import { cn } from "@/lib/utils";

function KillPills({ deal }: { deal: DealRow }) {
  const flags: string[] = [];
  if (deal.kill_no_legal_access) flags.push("no access");
  if (deal.kill_floodplain) flags.push("floodplain");
  if (deal.kill_dd_deadline_crunch) flags.push("DD clock");
  if (deal.checklist_kill > 0) flags.push(`${deal.checklist_kill} kill item`);
  if (flags.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-1" data-testid="kill-pills">
      {flags.map((f) => (
        <Badge key={f} tone="danger">
          {f}
        </Badge>
      ))}
    </span>
  );
}

/** §7.5 pipeline board by stage, plus a table. */
export function Deals() {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "table" ? "table" : "board";
  const q = params.get("q") ?? "";
  const kill = params.get("kill") === "true";

  const { data, isLoading, error } = useQuery({
    queryKey: ["deals", { q, kill, view }],
    queryFn: () => api.deals({ q: q || undefined, kill: kill || undefined, page_size: 200 }),
  });

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next);
  }

  return (
    <div className="p-4">
      <header className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Deals</h1>
          <p className="text-xs text-fg-muted">
            {data ? `${data.total} deal${data.total === 1 ? "" : "s"}` : "…"}
            {kill && " · showing only deals tripping a kill check"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            defaultValue={q}
            onKeyDown={(e) => {
              if (e.key === "Enter") setParam("q", (e.target as HTMLInputElement).value);
            }}
            placeholder="Filter name or county…"
            className={inputClass + " w-52"}
            data-testid="deals-filter"
          />
          <Button
            size="sm"
            variant={kill ? "danger" : "ghost"}
            onClick={() => setParam("kill", kill ? null : "true")}
            data-testid="deals-kill-filter"
          >
            Kill checks
          </Button>
          <div className="flex rounded border">
            <Button
              size="sm"
              variant={view === "board" ? "default" : "ghost"}
              onClick={() => setParam("view", "board")}
              data-testid="deals-view-board"
            >
              Board
            </Button>
            <Button
              size="sm"
              variant={view === "table" ? "default" : "ghost"}
              onClick={() => setParam("view", "table")}
              data-testid="deals-view-table"
            >
              Table
            </Button>
          </div>
        </div>
      </header>

      {isLoading && <Spinner />}
      {error && <ErrorNote error={error} />}

      {data && data.items.length === 0 && (
        <Card>
          <Empty
            title="No deals yet."
            hint="Promote a lead from its detail page once you are under contract."
          />
        </Card>
      )}

      {data && data.items.length > 0 && view === "board" && (
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="deal-board">
          {DEAL_STAGE_ORDER.map((stage) => {
            const inLane = data.items.filter((d) => d.stage === stage);
            if (inLane.length === 0 && ["closed_sale", "terminated"].includes(stage)) return null;
            return (
              <div
                key={stage}
                data-testid={`deal-lane-${stage}`}
                className="flex w-60 shrink-0 flex-col rounded-[var(--radius-card)] border bg-surface"
              >
                <header className="flex items-center justify-between border-b px-2 py-1.5">
                  <span className="text-xs font-semibold">{DEAL_STAGE_LABEL[stage]}</span>
                  <span className="text-[11px] tabular-nums text-fg-muted">{inLane.length}</span>
                </header>
                <ul className="flex-1 space-y-1 p-1.5">
                  {inLane.map((d) => (
                    <li key={d.id}>
                      <div className="rounded border bg-bg p-2">
                        <Link
                          to={`/deals/${d.id}`}
                          className="block text-xs font-medium hover:underline"
                        >
                          {d.name}
                        </Link>
                        <p className="mt-0.5 text-[11px] text-fg-muted">
                          {STRATEGY_LABEL[d.strategy]} · {formatAcres(d.parcel_acres)}
                        </p>
                        <dl className="mt-1 space-y-0.5 text-[11px]">
                          <div className="flex justify-between gap-2">
                            <dt className="text-fg-muted">Spread</dt>
                            <dd className="tabular-nums">
                              {formatMoneyCents(d.projected_spread_cents)}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt className="text-fg-muted">EV</dt>
                            <dd className="tabular-nums">
                              {formatMoneyCents(d.expected_value_cents)}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt className="text-fg-muted">Checklist</dt>
                            <dd className="tabular-nums">
                              {d.checklist_total - d.checklist_todo}/{d.checklist_total}
                            </dd>
                          </div>
                        </dl>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1">
                          {d.engagement_overdue_count > 0 && (
                            <Badge tone="warn">{d.engagement_overdue_count} overdue</Badge>
                          )}
                          <KillPills deal={d} />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {data && data.items.length > 0 && view === "table" && (
        <Card className="overflow-hidden">
          <div className="-mx-3 -my-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="deals-table">
              <thead>
                <tr className="border-b bg-surface-2 text-left text-xs font-semibold">
                  <th className="px-2 py-1.5">Deal</th>
                  <th className="px-2 py-1.5">Strategy</th>
                  <th className="px-2 py-1.5">Stage</th>
                  <th className="px-2 py-1.5 text-right">Contract</th>
                  <th className="px-2 py-1.5 text-right">Spread</th>
                  <th className="px-2 py-1.5 text-right">EV</th>
                  <th className="px-2 py-1.5 text-right">p(close)</th>
                  <th className="px-2 py-1.5">DD deadline</th>
                  <th className="px-2 py-1.5">Checklist</th>
                  <th className="px-2 py-1.5">Flags</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((d) => (
                  <tr key={d.id} className="border-b last:border-b-0 hover:bg-surface-2">
                    <td className="px-2 py-1.5">
                      <Link to={`/deals/${d.id}`} className="font-medium hover:underline">
                        {d.name}
                      </Link>
                    </td>
                    <td className="px-2 py-1.5 text-xs">{STRATEGY_LABEL[d.strategy]}</td>
                    <td className="px-2 py-1.5">
                      <Badge tone={dealStageTone(d.stage)}>{DEAL_STAGE_LABEL[d.stage]}</Badge>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {formatMoneyCents(d.contract_price_cents)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {formatMoneyCents(d.projected_spread_cents)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {formatMoneyCents(d.expected_value_cents)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {d.probability_of_close === null
                        ? "—"
                        : formatPct(Number(d.probability_of_close))}
                    </td>
                    <td
                      className={cn(
                        "px-2 py-1.5 text-xs tabular-nums",
                        d.kill_dd_deadline_crunch && "text-danger",
                      )}
                    >
                      {formatDate(d.dd_deadline)}
                    </td>
                    <td className="px-2 py-1.5 text-xs tabular-nums">
                      {d.checklist_total - d.checklist_todo}/{d.checklist_total}
                    </td>
                    <td className="px-2 py-1.5">
                      <KillPills deal={d} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
